import {
  Box,
  Button,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import MallScanner from "src/components/shared/ScanningInput";
import SearchFilterComp from "src/components/shared/SearchFilterComp";
import { userRoleType } from "src/constant";
import useAuthGlobal from "src/context/useAuthGlobal";
import useScanBulkReturnProcessing from "src/hooks/orders/bulk-return-processing/useScanBulkReturnProcessing";
import { ProductState, ProductStateList, selectList } from "src/utils/productState";
import BulkReturnListTable from "./BulkReturnListTable";
import useUpdateBulkReturnProcessing from "src/hooks/orders/bulk-return-processing/useUpdateBulkReturnProcessing";
import ManualBulkReturnForm from "./ManualBulkReturnForm";
import { Close } from "@mui/icons-material";
import useRemoveBulkReturnDetail from "src/hooks/orders/bulk-return-processing/useRemoveBulkReturnDetail";
import useAppGlobal from "src/context/useAppGlobal";
import useDetailBulkReturnProcessing from "src/hooks/orders/bulk-return-processing/useDetailBulkReturnProcessing";

const productInfoTemp = {
  type: ProductState.NORMAL,
};

export default function BulkReturnProcessingDialog({ returnedFormId, warehouseId, mutate: parentMutate }) {
  const { userMe } = useAuthGlobal();
  const { closeDialog } = useAppGlobal();
  const [type, setType] = useState(ProductState.NORMAL);
  const [productInfo, setProductInfo] = useState(productInfoTemp);
  const [itemCode, setItemCode] = useState();

  const { trigger: update } = useUpdateBulkReturnProcessing(returnedFormId);
  const { trigger: scan } = useScanBulkReturnProcessing(returnedFormId);
  const { trigger: deleteForm } = useRemoveBulkReturnDetail(returnedFormId);
  const { data: bulkReturnDetail, isLoading: detailLoading } = useDetailBulkReturnProcessing(returnedFormId);

  const handleFillReturnForm = (name, value) => {
    setProductInfo({ ...productInfo, [name]: value });
  };

  useEffect(() => {
    const onUpdate = async () => {
      const res = await update({
        body: {
          type: type,
          note: productInfo?.note,
          warehouse: warehouseId,
        },
      });
      if (!res?.success) {
        return enqueueSnackbar(res?.error?.message, { variant: "error" });
      }
    };
    if (type !== ProductState.NORMAL) {
      onUpdate();
    }
  }, [type, productInfo?.note, warehouseId, update]);

  useEffect(() => {
    const handleScan = async () => {
      const res = await scan({
        body: {
          itemCode: itemCode,
        },
      });
      if (res?.success) {
        parentMutate();
        return enqueueSnackbar("Thành công!", { variant: "success" });
      } else {
        return enqueueSnackbar(res?.error?.message, { variant: "error" });
      }
    };
    if (itemCode) {
      handleScan();
    }
  }, [itemCode, scan]);

  const onDelete = async () => {
    if (!bulkReturnDetail?.data?.returnLogDetails?.length) {
      enqueueSnackbar({
        variant: "warning",
        persist: true,
        content: (key) => (
          <Paper
            elevation={6}
            sx={{
              p: 2,
              backgroundColor: "var(--pending-txt)",
              color: "var(--black)",
              maxWidth: 360,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography fontWeight="bold">Bạn có muốn xoá thông tin nhập kho hiện tại không?</Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  closeSnackbar(key);
                }}
              >
                Hủy
              </Button>
              <Button
                size="small"
                sx={{ background: "var(--black)" }}
                variant="contained"
                onClick={() => {
                  closeSnackbar(key);
                  handleDelete();
                }}
              >
                Đồng ý
              </Button>
            </Box>
          </Paper>
        ),
      });
    } else {
      closeDialog();
    }
  };

  const handleDelete = async () => {
    const res = await deleteForm();
    if (res?.success) {
      closeDialog();
      return enqueueSnackbar("Thành công!", { variant: "success" });
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    }
  };

  useEffect(() => {
    if (!!bulkReturnDetail?.data?.type) {
      setType(bulkReturnDetail?.data?.type);
    }
  }, [bulkReturnDetail?.data?.type]);

  return (
    <>
      <DialogTitle
        display="flex"
        justifyContent="space-between"
      >
        <Typography
          fontWeight={700}
          variant="h3"
        >
          Trả hàng hàng loạt
        </Typography>
        <IconButton
          onClick={onDelete}
          variant="contained"
          sx={{ bgcolor: "var(--light-grey) !important" }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid
          py={1}
          container
          sx={{ height: "80vh", display: "flex", justifyContent: "space-between" }}
        >
          <Grid
            xs={5}
            sx={{
              height: "100%",
              overflowY: "auto",
            }}
          >
            <Box
              pr={1}
              display="flex"
              gap={1}
              flexDirection="column"
            >
              <FormControl>
                <FormLabel sx={{ fontWeight: 700 }}>Tình trạng hàng:</FormLabel>
                <RadioGroup
                  key={detailLoading}
                  sx={{ display: "flex", flexDirection: "row" }}
                  aria-labelledby={`${1}-label`}
                  name="radio-buttons-group"
                  defaultValue={type}
                  onChange={(e) => {
                    handleFillReturnForm("type", e.target.value);
                    setType(e.target.value);
                  }}
                >
                  {ProductStateList.map((v) => {
                    return (
                      <FormControlLabel
                        key={v.id}
                        value={v.id}
                        control={<Radio />}
                        label={v.title}
                      />
                    );
                  })}
                </RadioGroup>
              </FormControl>
              {[ProductState.NORMAL, ProductState.DAMAGED].includes(type) ? (
                <Box>
                  <Box mb={1.5}>
                    <SearchFilterComp
                      disabled={userMe?.role?.code !== userRoleType.superAdmin?.toLowerCase()}
                      size="default"
                      width="100%"
                      minWidth={"100px"}
                      defaultValue={productInfo?.warehouse ? productInfo?.warehouse : warehouseId}
                      selectList={selectList}
                      autoComplete={{
                        warehouse: {
                          limit: 20,
                          skip: 0,
                          endpoint: "/api/v1/warehouses",
                          state: (e) => handleFillReturnForm("warehouse", e),
                          searchField: "name",
                          label: "Kho nhập hàng trả",
                        },
                      }}
                    />
                  </Box>
                  <TextField
                    onChange={(e) => handleFillReturnForm("note", e.target.value)}
                    label="Ghi chú"
                    multiline
                    fullWidth
                    rows={3}
                    sx={{ mb: 2 }}
                  />

                  <MallScanner
                    getIndividualValue={setItemCode}
                    individualValue={itemCode}
                    label="Scan mã sản phẩm"
                    style={{ mb: 1.5 }}
                  />
                </Box>
              ) : (
                <ManualBulkReturnForm
                  returnedFormId={returnedFormId}
                  warehouseId={warehouseId}
                />
              )}
            </Box>
          </Grid>
          <Grid xs={6.8}>
            <BulkReturnListTable returnedFormId={returnedFormId} />
          </Grid>
        </Grid>
      </DialogContent>
    </>
  );
}
